import { Button } from '@shared/components';
import { CircularProgress } from '@shared/components/plugin-components';
import { useToast } from '@shared/components/toast';
import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import { useExtensionMessagingContext } from '@shared/contexts/useExtensionMessagingContext';
import { usePageInfo } from '@shared/features/sidekick-navigator/PageInfoContextProvider';
import {
  canBuildShipment,
  getTMSShipmentUrls,
} from '@shared/features/sidekick-navigator/tms';
import { useBuildShipmentMutation } from '@shared/generated/graphql';
import { useState } from 'react';
import { LiaPlusSolid } from 'react-icons/lia';
import { toTMS } from './TMSSidekick';
import { getRefetchDomPromise } from './refetchDom';

type AddShipmentButtonProps = {
  parentUrl: string;
  referenceNumber: string;
};

export function AddShipmentButton({
  parentUrl,
  referenceNumber,
}: AddShipmentButtonProps) {
  const { defaultOrgId } = useMeContext();
  const { pageInfo } = usePageInfo();
  const { sendToast } = useToast();
  const { refetchDom } = useExtensionMessagingContext();
  const [buildShipment, { loading: mutationLoading }] =
    useBuildShipmentMutation();
  const [domFetchLoading, setDomFetchLoading] = useState<boolean>(false);
  const tms = toTMS(pageInfo);

  async function buildShipmentFromDom() {
    if (!defaultOrgId) {
      sendToast('No organization found');
      return;
    }
    if (!referenceNumber) {
      sendToast('No shipment reference number found');
      return;
    }

    const shipmentUrls = getTMSShipmentUrls(tms, parentUrl, referenceNumber);
    const domTimeout = shipmentUrls === undefined ? 1000 : 10000;

    const p = {
      domTimeout,
      shipmentUrls,
      refetchDom,
    };

    setDomFetchLoading(true);
    const doms = await getRefetchDomPromise(p).finally(() =>
      setDomFetchLoading(false)
    );

    const domContent = doms?.join('\n') || '';

    await buildShipment({
      variables: {
        input: {
          organizationId: defaultOrgId,
          externalId: referenceNumber,
          data: domContent,
          integration: tms,
        },
      },
      refetchQueries: ['FindShipment'],
    }).catch((e) => {
      sendToast('Failed to build shipment', {
        description: e.message,
        variant: 'error',
      });
    });
  }

  const loading = mutationLoading || domFetchLoading;

  const DOM_LOADING_MESSAGE = 'Retrieving load details...';
  const MUTATION_LOADING_MESSAGE = 'Building shipment...';
  const ADD_SHIPMENT_MESSAGE = 'Add shipment';

  const buttonText = domFetchLoading
    ? DOM_LOADING_MESSAGE
    : mutationLoading
    ? MUTATION_LOADING_MESSAGE
    : ADD_SHIPMENT_MESSAGE;

  if (!canBuildShipment(pageInfo)) return null;

  return (
    <div className="flex h-full w-full flex-row items-start justify-center">
      <Button
        onPress={buildShipmentFromDom}
        size="xs"
        variant="secondary"
        icon={
          loading ? (
            <div className="mr-2">
              <CircularProgress size="sm" />
            </div>
          ) : (
            <LiaPlusSolid />
          )
        }
        isDisabled={loading}
      >
        {buttonText}
      </Button>
    </div>
  );
}
